import {
  getIntegrationDisplayName,
  normalizeToolkitKey,
} from '@littlebird/ui-utils';

import { AppleIMessageIcon } from '@/assets/icons/apple-imessage';
import { AppleMailIcon } from '@/assets/icons/apple-mail';
import { AppleNotesIcon } from '@/assets/icons/apple-notes';
import {
  AsanaIcon,
  GithubIcon,
  LinearIcon,
  NotionIcon,
} from '@/assets/icons/brand-mark-icons';
import { CalendlyIcon } from '@/assets/icons/calendly';
import { ComputerUseIcon } from '@/assets/icons/computer-use';
import { FantasticalIcon } from '@/assets/icons/fantastical';
import { Icons } from '@/assets/icons/icons';
import { LogseqIcon } from '@/assets/icons/logseq';
import { ObsidianIcon } from '@/assets/icons/obsidian';
import { OmniFocusIcon } from '@/assets/icons/omnifocus';
import { TablePlusIcon } from '@/assets/icons/tableplus';
import { ThingsIcon } from '@/assets/icons/things';
import { cn } from '@/lib/utils';

export { getIntegrationDisplayName, normalizeToolkitKey };

export const integrationIconMap: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  gmail: Icons.gmail,
  // Keyed without the hyphen — `normalizeToolkitKey` below strips non-alphanumerics
  // from lookups, so `getBrandIcon('apple-mail')` resolves to `applemail`.
  applemail: AppleMailIcon,
  applenotes: AppleNotesIcon,
  calendly: CalendlyIcon,
  computeruse: ComputerUseIcon,
  googlecalendar: Icons.googleCalendar,
  googledocs: Icons.googleDocs,
  googledrive: Icons.googleDrive,
  googletasks: Icons.googleTasks,
  googleforms: Icons.googleForms,
  dropbox: Icons.dropbox,
  googlesheets: Icons.googleSheets,
  eventkit: Icons.appleCalendar,
  applecalendar: Icons.appleCalendar,
  macoscalendar: Icons.appleCalendar,
  applereminders: Icons.appleReminders,
  applecontacts: Icons.appleContacts,
  imessage: AppleIMessageIcon,
  outlook: Icons.outlook,
  microsoft_teams: Icons.microsoftTeams,
  // The on-device Outlook server; same brand, separate template id.
  outlooklocal: Icons.outlook,
  slack: Icons.slack,
  todoist: Icons.todoist,
  linkedin: Icons.linkedin,
  twitter: Icons.twitter,
  notion: NotionIcon,
  linear: LinearIcon,
  github: GithubIcon,
  asana: AsanaIcon,
  fantastical: FantasticalIcon,
  filesystem: Icons.folderTree,
  terminal: Icons.terminal,
  logseq: LogseqIcon,
  obsidian: ObsidianIcon,
  omnifocus: OmniFocusIcon,
  tableplus: TablePlusIcon,
  things: ThingsIcon,
  zohocrm: Icons.zoho, // "Zoho CRM"
  zoho_crm: Icons.zoho, // MCP slug "Zoho_crm"
};

const monogramCache = new Map<
  string,
  React.ComponentType<{ className?: string }>
>();

const getMonogramIcon = (
  displayName: string
): React.ComponentType<{ className?: string }> => {
  const letter = displayName.trim().charAt(0).toUpperCase() || '?';
  const cached = monogramCache.get(letter);
  if (cached) return cached;
  const MonogramIcon = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 24 24" className={cn('shrink-0 rounded', className)}>
      <rect
        width="24"
        height="24"
        className="[fill:var(--secondary-default)]"
      />
      <text
        x="12"
        y="12"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="13"
        fontWeight="500"
        className="[fill:var(--foreground-muted)]"
      >
        {letter}
      </text>
    </svg>
  );
  MonogramIcon.displayName = `MonogramIcon(${letter})`;
  monogramCache.set(letter, MonogramIcon);
  return MonogramIcon;
};

export const getIntegrationIcon = (
  name: string
): React.ComponentType<{ className?: string }> => {
  const match = integrationIconMap[normalizeToolkitKey(name)];
  if (match) return match;
  return getMonogramIcon(name);
};

/** Like `getIntegrationIcon` but returns undefined instead of a monogram when
 * there's no known brand icon — lets callers fall back to their own default. */
export const getBrandIcon = (
  name: string
): React.ComponentType<{ className?: string }> | undefined =>
  integrationIconMap[normalizeToolkitKey(name)];
